import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./client";

export type AuditRow = {
  id: string;
  tenant_id: string;
  actor_user_id: string;
  actor_full_name: string | null;
  actor_email: string | null;
  actor_branch_name: string | null;
  entity_type: string;
  entity_id: string;
  action_type: string;
  created_at: string;
};

export type AuditDetail = AuditRow & {
  before_data: Record<string, unknown> | null;
  after_data: Record<string, unknown> | null;
};

export type AuditListResponse = {
  rows: AuditRow[];
  total: number;
  limit: number;
  offset: number;
};

export type AuditFacets = {
  entity_types: { value: string; count: number }[];
  action_types: { value: string; count: number }[];
  actors: {
    user_id: string;
    full_name: string | null;
    email: string | null;
    branch_name: string | null;
    count: number;
  }[];
};

export type AuditFilter = {
  entityType?: string;
  entityId?: string;
  actionType?: string;
  actorUserId?: string;
  startDate?: string;
  endDate?: string;
  q?: string;
  limit?: number;
  offset?: number;
};

function buildQuery(filter: AuditFilter): Record<string, string> {
  const q: Record<string, string> = {};
  if (filter.entityType) q.entity_type = filter.entityType;
  if (filter.entityId) q.entity_id = filter.entityId;
  if (filter.actionType) q.action_type = filter.actionType;
  if (filter.actorUserId) q.actor_user_id = filter.actorUserId;
  if (filter.startDate) q.start_date = filter.startDate;
  if (filter.endDate) q.end_date = filter.endDate;
  if (filter.q) q.q = filter.q;
  if (filter.limit !== undefined) q.limit = String(filter.limit);
  if (filter.offset !== undefined) q.offset = String(filter.offset);
  return q;
}

export function useAuditList(filter: AuditFilter): {
  data: AuditListResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [data, setData] = useState<AuditListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const queryKey = JSON.stringify(buildQuery(filter));

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<AuditListResponse>("/audit-logs", buildQuery(filter), {
        signal: controller.signal,
      })
      .then((res) => setData(res))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Denetim kayitlari Yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  return { data, loading, error, reload };
}

export function useAuditFacets(): {
  data: AuditFacets | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [data, setData] = useState<AuditFacets | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<AuditFacets>("/audit-logs/facets", undefined, { signal: controller.signal })
      .then((res) => setData(res))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Filtre listesi Yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  return { data, loading, error, reload };
}

export async function fetchAuditDetail(id: string): Promise<AuditDetail> {
  return api.get<AuditDetail>(`/audit-logs/${id}`);
}
